import { LocalDateTime } from 'js-joda';
import { oc } from 'ts-optchain';
import _ from 'lodash';

import * as TableBasedTemplates from 'types/tableBasedTemplates';
import {
  BookingRequest,
  DateTimeOccupancyInfo,
  DateTimeOccupancyMap,
  OccupiedState,
} from 'types/flexibleInventory';
import { NO_TICKET_TYPE_ID_FOR_TURN } from 'constants/TableBasedTemplatesConstants';
import { WalkInCalendarParty } from 'types';
import {
  adjustForBusinessDay,
  fastParseDateTime,
  get15MinTimeIntervals,
  getBusinessDay,
} from 'utils/timeUtils';
import { core } from '@tock/coretypes';
import {
  findRelevantTurnTime,
  getStartTimeToMinimumTurnTimeMap,
} from 'utils/tableBasedTemplatesUtils';

export const hasLastBookableTime = (ticketGroup: core.TicketGroup) =>
  !!ticketGroup.lastBookableTime;

export const getTicketGroupEndTime = (ticketGroup: core.TicketGroup) =>
  ticketGroup.lastBookableTime || ticketGroup.finishedByTime;

export const isFlexTicketGroup = (ticketGroup: core.TicketGroup) =>
  !!getTicketGroupEndTime(ticketGroup);

/**
 * @param finishedByTime The time at which all parties need to be finished
 * @param tableVariety The variety of the table of the ticket group
 * @returns Whether the ticket group supports multiple purchases
 */
export function canSupportMultiplePurchases({
  finishedByTime,
  tableVariety,
}: {
  finishedByTime?: string;
  tableVariety: core.TableType.Variety;
}): boolean {
  return tableVariety === 'COMMUNAL_TABLE' || !!finishedByTime;
}

/**
 * Annotates the list of bookable times of a ticket group with info about whether that slot is available to be booked
 *
 * @param ticketGroup The ticket group for which we want to generate occupancy details
 * @param turnTimes A map of turn time details
 * @returns A sorted list of bookable times for the ticket group tagged with their occupancy state
 */
export function generateDateTimeOccupancyInfosForTicketGroup(
  ticketGroup: core.TicketGroup,
  turnTimes: TableBasedTemplates.TurnTimes
): DateTimeOccupancyInfo[] {
  if (!isFlexTicketGroup(ticketGroup)) {
    return [];
  }
  const availableDateTimes = getBookableIntervalsForFlexibleTicketGroup(ticketGroup);
  const occupiedTimes = getOccupiedTimesForFlexibleTicketGroup(ticketGroup, turnTimes);

  // TODO(jacob, 2019-01-21): This is based on ticket group. Is it correct?
  const ticketTypeId = _.isNumber(ticketGroup.ticketType[0].id)
    ? ticketGroup.ticketType[0].id
    : undefined;
  const startTimeToMinimumTurnTimeMap = getStartTimeToMinimumTurnTimeMap(
    turnTimes,
    ticketGroup.minPurchaseSize,
    ticketGroup.maxPurchaseSize,
    ticketTypeId as number
  );
  return generateDateTimeOccupancyInfos(
    availableDateTimes,
    occupiedTimes,
    startTimeToMinimumTurnTimeMap
  );
}

export function generateDateTimeOccupancyInfos(
  availableDateTimes: LocalDateTime[],
  occupiedTimes: DateTimeOccupancyMap,
  minTurnTimes: { [localTime: string]: number }
): DateTimeOccupancyInfo[] {
  const lastDateTime = _.last(availableDateTimes);
  if (!lastDateTime) {
    return [];
  }

  return _.map(availableDateTimes, (localDateTime) => {
    let occupiedState: OccupiedState = {
      type: 'AVAILABLE',
    };
    const minTurnTimeMinutes = minTurnTimes[localDateTime.toLocalTime().toString()];
    const endOfTurn = minTurnTimeMinutes
      ? localDateTime.plusMinutes(minTurnTimeMinutes !== Infinity ? minTurnTimeMinutes : 0)
      : undefined;

    if (endOfTurn) {
      for (
        let currentStep = localDateTime;
        currentStep.isBefore(endOfTurn);
        currentStep = currentStep.plusMinutes(15)
      ) {
        const occupiedBy = occupiedTimes[currentStep.toString()];
        if (occupiedBy) {
          occupiedState =
            currentStep === localDateTime ? occupiedBy.occupiedState : { type: 'BLOCKED' };
          break;
        } else if (currentStep.isAfter(lastDateTime)) {
          occupiedState = { type: 'BLOCKED' };
          break;
        }
      }
    }
    return {
      localDateTime,
      occupiedState,
    };
  });
}

/**
 * Used to generate a list of bookable times for a flexible time ticket group
 *
 * @param ticketGroup The ticket group for which we want the bookable range
 * @returns A list of LocalDateTimes for every 15 minutes that's bookable for the ticket group
 */
export function getBookableIntervalsForFlexibleTicketGroup(
  ticketGroup: core.TicketGroup
): LocalDateTime[] {
  const endTime = getTicketGroupEndTime(ticketGroup);
  if (!endTime) {
    return [];
  }
  const ticketGroupDateTime = fastParseDateTime(ticketGroup.dateTime);
  const finishedBy = getFinishedByDateTime(ticketGroupDateTime, endTime);
  return getBookableIntervalsForRange(
    {
      startDateTime: ticketGroupDateTime,
      finishedByDateTime: finishedBy,
    },
    hasLastBookableTime(ticketGroup)
  );
}

export function getFinishedByDateTime(
  ticketGroupDateTime: LocalDateTime,
  finishedByTime: string
): LocalDateTime {
  const businessDay = getBusinessDay(ticketGroupDateTime);
  return adjustForBusinessDay(businessDay.toString(), finishedByTime);
}

/**
 * Create 15 min time intervals for a flex table.
 * Optionally include the finishedByTime in the range.
 * The finishedByTime is included in the range when the end time should be bookable
 */
export function getBookableIntervalsForRange(
  {
    startDateTime,
    finishedByDateTime,
  }: {
    startDateTime: LocalDateTime;
    finishedByDateTime: LocalDateTime;
  },
  isFinishedByInclusive = false
) {
  const businessDay = getBusinessDay(startDateTime);
  return _.filter(get15MinTimeIntervals(businessDay), (localDateTime) => {
    return (
      !localDateTime.isBefore(startDateTime) &&
      (localDateTime.isBefore(finishedByDateTime) ||
        (isFinishedByInclusive && localDateTime.equals(finishedByDateTime)))
    );
  });
}

/**
 * Generates a map of time in the ticket group's bookable range to whether that time is unavailable
 * due to the presence of a purchase or locked subset at that time
 *
 * @param ticketGroup The ticket group for which we want to generate occupied times
 * @param turnTimes A map of turn time information
 * @returns A map of time to a piece of occupancy info for each booked or locked time
 */
export function getOccupiedTimesForFlexibleTicketGroup(
  ticketGroup: core.TicketGroup,
  turnTimes: TableBasedTemplates.TurnTimes
): DateTimeOccupancyMap {
  return getOccupiedTimesForFlexibleEntity(
    {
      purchases: ticketGroup.purchaseDetail as core.PastPurchase[],
      ticketSubsets: ticketGroup.ticketSubset as core.TicketSubset[],
    },
    turnTimes,
    ticketGroup.ticketType[0].id as number
  );
}
export function getOccupiedTimesForFlexibleEntity(
  {
    purchases,
    ticketSubsets,
    walkIns,
  }: {
    purchases: core.PastPurchase[];
    ticketSubsets: core.TicketSubset[];
    walkIns?: WalkInCalendarParty[];
  },
  turnTimes: TableBasedTemplates.TurnTimes,
  defaultTicketTypeId: number
): DateTimeOccupancyMap {
  const occupiedTimes: DateTimeOccupancyMap = {};
  _.forEach(ticketSubsets, (ticketSubset: core.TicketSubset) => {
    if (ticketSubset.state !== 'LOCKED' || !ticketSubset.requestedDateTime) {
      return;
    }
    const requestedDateTime = fastParseDateTime(ticketSubset.requestedDateTime);
    let bookingRequest;
    if (ticketSubset.purchaseVerifyInfo) {
      const purchaseVerifyTicketTypeId = ticketSubset.purchaseVerifyInfo.ticketTypeId;
      bookingRequest = {
        requestedDateTime,
        ticketCount: ticketSubset.ticketCount,
        ticketTypeId:
          purchaseVerifyTicketTypeId || ticketSubset.ticketTypeId || defaultTicketTypeId,
      };
    } else {
      bookingRequest = {
        requestedDateTime,
        ticketCount: ticketSubset.ticketCount,
        ticketTypeId: ticketSubset.ticketTypeId || defaultTicketTypeId,
      };
    }
    addToOccupiedTimes(occupiedTimes, turnTimes, bookingRequest, {
      type: 'LOCKED',
      ticketSubset,
    });
  });
  _.forEach(purchases, (purchaseDetail: core.PastPurchase) => {
    const requestedDateTime = fastParseDateTime(purchaseDetail.ticketDateTime);
    const actualTurnTimeMins =
      Math.ceil(Math.round(oc(purchaseDetail).visit.turnTimeSec(0) / 60) / 15) * 15;
    const turnTimeMinsOverride =
      Math.ceil(Math.round(oc(purchaseDetail).visit.turnTimeSecOverride(0) / 60) / 15) * 15;
    const bookingRequest = {
      requestedDateTime,
      ticketCount: purchaseDetail.ticketCount,
      ticketTypeId: purchaseDetail.ticketTypeId,
      turnTimeMinutes: actualTurnTimeMins || turnTimeMinsOverride,
    };
    addToOccupiedTimes(occupiedTimes, turnTimes, bookingRequest, {
      type: 'BOOKED',
      purchase: purchaseDetail,
    });
  });
  _.forEach(walkIns, (walkIn: WalkInCalendarParty) => {
    const requestedDateTime = fastParseDateTime(walkIn.dateTime15MinuteInterval);
    // 2019/05/02 KM: what should this be?
    const { party } = walkIn;
    let actualTurnTimeMins = undefined;
    if (party.seatedTime && party.leftTime) {
      actualTurnTimeMins = Math.ceil(Math.round(party.leftTime - party.seatedTime) / 60 / 15) * 15;
    }
    const bookingRequest = {
      requestedDateTime,
      ticketCount: party.partySize,
      ticketTypeId: NO_TICKET_TYPE_ID_FOR_TURN,
      turnTimeMinutes: actualTurnTimeMins,
    };
    addToOccupiedTimes(occupiedTimes, turnTimes, bookingRequest, {
      type: 'BOOKED',
      walkIn,
    });
  });
  return occupiedTimes;
}

/**
 * Used to build up the full occupancy map for a ticket group
 *
 * @param occupiedTimes The current set of occupied times
 * @param turnTimes A map of turn time information
 * @param bookingRequest Details about the "booking request" that exists
 * @param semanticOccupiedState The kind of occupancy of the booking request
 * @returns An updated occupied map with the booking request accounted for
 */
export function addToOccupiedTimes(
  occupiedTimes: DateTimeOccupancyMap,
  turnTimes: TableBasedTemplates.TurnTimes,
  bookingRequest: BookingRequest,
  semanticOccupiedState: OccupiedState
): DateTimeOccupancyMap {
  const { requestedDateTime, ticketCount, ticketTypeId } = bookingRequest;
  const turnTime = findRelevantTurnTime(turnTimes, {
    partySize: ticketCount,
    startDateTime: requestedDateTime,
    ticketTypeId,
  });
  // TODO (YK 2018/09/12): Handle if the value is already set. Prioritize LOCKED or SOLD above BLOCKED
  occupiedTimes[requestedDateTime.toString()] = {
    localDateTime: requestedDateTime,
    occupiedState: semanticOccupiedState,
  };

  if (!turnTime && !bookingRequest.turnTimeMinutes) {
    return occupiedTimes;
  }

  const turnTimeMinutes = bookingRequest.turnTimeMinutes || oc(turnTime).expectedTurnTimeMinutes(0);
  const anticipatedFinishedByTime = requestedDateTime.plusMinutes(turnTimeMinutes);

  let cursor = requestedDateTime.plusMinutes(15);
  while (cursor.isBefore(anticipatedFinishedByTime)) {
    occupiedTimes[cursor.toString()] = {
      localDateTime: cursor,
      occupiedState: {
        type: 'BLOCKED',
      },
    };
    cursor = cursor.plusMinutes(15);
  }
  return occupiedTimes;
}

/**
 * Returns the "leader" table for a booking. If a booking has more than one
 * table, the leader is the single table that is drawn fully-saturated in detail
 * mode.
 */
export function getLeaderTableIdForBooking(purchase: core.PastPurchase): number | null {
  if (_.isEmpty(purchase.tableId)) {
    return null;
  }
  return purchase.tableId[0];
}
